import React from 'react';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import clsx from 'clsx';

import BaseFilter from '@/components/common-components/BaseFilter/BaseFilter';
import FilterPill from '@/components/common-components/BaseFilter/FilterPill';
import AddCircleOutline from '@/components/Icons/icons/AddCircleOutline';
import XIcon from '@/components/Icons/icons/XIcon';

import {
  SOCIAL_LISTENING_FILTER_OPTIONS,
  SOCIAL_LISTENING_FILTERS,
  SOCIAL_LISTENING_SORT_LABEL_MAP,
} from '../../constants';
import { useCampaignDetail } from './CampaignDetailProvider';
import CampaignSortSelect from './CustomDetailHeader/CampaignSortSelect';
import CampaignTypeSelect from './CustomDetailHeader/CampaignTypeSelect';
import ContentTypeSelect, { allContentTypeOptions } from './CustomDetailHeader/ContentTypeSelect';
import LabelTypeSelect from './CustomDetailHeader/LabelTypeSelect';
import ListTypeSelect from './CustomDetailHeader/ListTypeSelect';
import { usageRightsStatusOptions } from './CustomDetailHeader/UsageRightsHeader';
import SocialFilterChip from './SocialFilterChip';

const AddFilterButton = ({ onClick, isOpen }) => {
  return (
    <div className={clsx('add-filter-button', { 'add-filter-button__active': isOpen })} onClick={onClick} role="button">
      <AddCircleOutline width={14} height={14} color="var(--text-highlight)" />
      Add Filter
    </div>
  );
};

const AppliedChipSection = () => {
  const {
    selectedContentType,
    selectedPlatformType,
    onRemoveFilter,
    activeTab,
    content,
    selectedLists,
    setSelectedLists,
    selectedCampaigns,
    setSelectedCampaigns,
    usageRightsContentType,
    usageRightsStatus,
    setNewUsageRequestsContentType,
    setNewUsageRequestsStatus,
    clearAllContentFilters,
    clearAllUsageRequestsFilters,
    selectedLabels,
    sortField,
    setSortField,
    sortOrder,
    setSortOrder,
    getAppliedFilters,
    activeFilterKey,
    setActiveFilterKey,
    setNewLabels,
  } = useCampaignDetail();

  if (activeTab === 'stat') return <></>;

  const appliedFilters = getAppliedFilters();

  function renderUsageRightsChips() {
    return (
      <>
        {usageRightsContentType !== 'all' && (
          <SocialFilterChip
            title="Content"
            value={
              <>
                <span style={{ marginLeft: '5px', marginRight: '5px' }}>
                  {allContentTypeOptions.find((option) => option.value === usageRightsContentType).logo}
                </span>
                {allContentTypeOptions.find((option) => option.value === usageRightsContentType).text}
              </>
            }
            onCrossClick={() => setNewUsageRequestsContentType('all')}
          />
        )}
        {usageRightsStatus !== 'all' && (
          <SocialFilterChip
            title="Status"
            value={
              <span
                style={{
                  color: usageRightsStatusOptions.find((option) => option.value === usageRightsStatus).dotColor,
                }}>
                {usageRightsStatusOptions.find((option) => option.value === usageRightsStatus).text}
              </span>
            }
            onCrossClick={() => setNewUsageRequestsStatus('all')}
          />
        )}
      </>
    );
  }

  function renderContentChips() {
    return (
      <>
        {(selectedContentType !== 'all' || activeFilterKey === SOCIAL_LISTENING_FILTERS.CONTENT_TYPE) && (
          <FilterPill
            filterLabel="Content Type"
            selected={
              selectedContentType !== 'all'
                ? [allContentTypeOptions.find((option) => option.value === selectedContentType)?.text]
                : []
            }
            OptionsComponent={ContentTypeSelect}
            onRemoveFilter={() => {
              onRemoveFilter(SOCIAL_LISTENING_FILTERS.CONTENT_TYPE);
              setActiveFilterKey(null);
            }}
            isOpen={activeFilterKey === SOCIAL_LISTENING_FILTERS.CONTENT_TYPE}
            onClose={() => setActiveFilterKey(null)}
            popoverWidth={204}
          />
        )}
        {(selectedCampaigns.length > 0 || activeFilterKey === SOCIAL_LISTENING_FILTERS.CAMPAIGN) && (
          <FilterPill
            filterLabel="Campaign"
            selected={selectedCampaigns.map((campaign) => campaign.name)}
            OptionsComponent={CampaignTypeSelect}
            onRemoveFilter={() => {
              onRemoveFilter(SOCIAL_LISTENING_FILTERS.CAMPAIGN);
              setActiveFilterKey(null);
            }}
            onResetSelection={() => {
              setSelectedCampaigns([]);
              setActiveFilterKey(SOCIAL_LISTENING_FILTERS.CAMPAIGN);
            }}
            isOpen={activeFilterKey === SOCIAL_LISTENING_FILTERS.CAMPAIGN}
            onClose={() => setActiveFilterKey(null)}
            popoverWidth={310}
          />
        )}
        {(selectedLists.length > 0 || activeFilterKey === SOCIAL_LISTENING_FILTERS.LISTS) && (
          <FilterPill
            filterLabel="Lists"
            selected={selectedLists.map((list) => list.name)}
            OptionsComponent={ListTypeSelect}
            onRemoveFilter={() => {
              onRemoveFilter(SOCIAL_LISTENING_FILTERS.LISTS);
              setActiveFilterKey(null);
            }}
            onResetSelection={() => {
              setSelectedLists([]);
              setActiveFilterKey(SOCIAL_LISTENING_FILTERS.LISTS);
            }}
            isOpen={activeFilterKey === SOCIAL_LISTENING_FILTERS.LISTS}
            onClose={() => setActiveFilterKey(null)}
            popoverWidth={310}
          />
        )}
        {(selectedLabels?.length > 0 ||
          selectedLabels === -1 ||
          activeFilterKey === SOCIAL_LISTENING_FILTERS.LABELS) && (
          <FilterPill
            filterLabel="Labels"
            selected={
              selectedLabels === -1 ? ["Doesn't Contain Label"] : selectedLabels?.map((label) => label.title) || []
            }
            OptionsComponent={LabelTypeSelect}
            onRemoveFilter={() => {
              onRemoveFilter(SOCIAL_LISTENING_FILTERS.LABELS);
              setActiveFilterKey(null);
            }}
            onResetSelection={() => {
              setNewLabels([]);
              setActiveFilterKey(SOCIAL_LISTENING_FILTERS.LABELS);
            }}
            isOpen={activeFilterKey === SOCIAL_LISTENING_FILTERS.LABELS}
            onClose={() => setActiveFilterKey(null)}
          />
        )}
        {(sortField !== 'all' || activeFilterKey === SOCIAL_LISTENING_SORT_LABEL_MAP.SORT_BY) && (
          <FilterPill
            mode="sort"
            filterLabel={SOCIAL_LISTENING_SORT_LABEL_MAP[sortField]}
            leftIcon={
              sortOrder === 'desc' ? (
                <ArrowUpward htmlColor="var(--icon-highlight)" style={{ width: 20, height: 20 }} />
              ) : (
                <ArrowDownward htmlColor="var(--icon-highlight)" style={{ width: 20, height: 20 }} />
              )
            }
            OptionsComponent={CampaignSortSelect}
            onRemoveFilter={() => {
              onRemoveFilter(SOCIAL_LISTENING_FILTERS.SORT_BY);
              setActiveFilterKey(null);
            }}
            isOpen={activeFilterKey === SOCIAL_LISTENING_FILTERS.SORT_BY}
            onClose={() => setActiveFilterKey(null)}
            popoverWidth={180}
          />
        )}
        {appliedFilters.length > 0 && (
          <BaseFilter
            filterTitle="Filter posts by:"
            triggerComponent={({ isOpen }) => <AddFilterButton onClick={() => {}} isOpen={isOpen} />}
            options={SOCIAL_LISTENING_FILTER_OPTIONS.filter((option) => !appliedFilters.includes(option.key))}
            handleInsertFilter={({ key }) => setActiveFilterKey(key)}
            appliedFiltersKey={appliedFilters}
          />
        )}
      </>
    );
  }

  function renderTabChips() {
    switch (activeTab) {
      case 'content':
        return renderContentChips();
      case 'usage-rights':
        return renderUsageRightsChips();
      default:
        return <></>;
    }
  }

  function renderClearAllButton() {
    let showButton = false;
    let onClick = () => {};

    switch (activeTab) {
      case 'content':
        if (
          selectedPlatformType !== 'all' ||
          selectedContentType !== 'all' ||
          selectedLists?.length ||
          selectedCampaigns?.length ||
          selectedLabels?.length ||
          sortField !== 'all'
        ) {
          showButton = true;
          onClick = clearAllContentFilters;
        }
        break;
      case 'usage-rights':
        if (usageRightsStatus !== 'all' || usageRightsContentType !== 'all') {
          showButton = true;
        }
        onClick = clearAllUsageRequestsFilters;
        break;
      default:
        return <></>;
    }
    if (!showButton) {
      return <></>;
    }
    return (
      <div className="clear-all-button" onClick={onClick}>
        <XIcon color="var(--icon-default)" height={24} width={24} />
        Clear All
      </div>
    );
  }

  return (
    <div
      className={clsx('sl-applied-chip-section-container', {
        'sl-applied-chip-section-container__active': appliedFilters.length > 0,
      })}>
      <div className="sl-applied-chip-section">{renderTabChips()}</div>
      {renderClearAllButton()}
    </div>
  );
};

export default AppliedChipSection;
